import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { settingsService } from '../../services/dataService';
import styles from './SettingsErrorLogsPage.module.css';

const PAGE_SIZE = 50;

const toIso = (value) => (value ? new Date(value).toISOString() : undefined);

const SettingsErrorLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [resource, setResource] = useState('');
  const [user, setUser] = useState('');
  const [clientIp, setClientIp] = useState('');
  const [callingUrl, setCallingUrl] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [filters, setFilters] = useState({ resource: '', user: '', clientIp: '', callingUrl: '', startTime: '', endTime: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await settingsService.getErrorLogs({
        resource: filters.resource || undefined,
        user: filters.user || undefined,
        client_ip: filters.clientIp || undefined,
        calling_url: filters.callingUrl || undefined,
        start_time: toIso(filters.startTime),
        end_time: toIso(filters.endTime),
        page,
        page_size: PAGE_SIZE,
      });
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (startTime && endTime && new Date(endTime) < new Date(startTime)) {
      setError('End time must be on or after start time');
      return;
    }
    setPage(1);
    setFilters({ resource: resource.trim(), user: user.trim(), clientIp: clientIp.trim(), callingUrl: callingUrl.trim(), startTime, endTime });
  };

  const clearFilters = () => {
    setResource('');
    setUser('');
    setClientIp('');
    setCallingUrl('');
    setStartTime('');
    setEndTime('');
    setPage(1);
    setFilters({ resource: '', user: '', clientIp: '', callingUrl: '', startTime: '', endTime: '' });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>API Error Logs</h1>
          <p>Failed requests and their diagnostic details.</p>
        </div>
        <Link className={styles.backLink} to="/settings">Back to Settings</Link>
      </header>

      <section className={styles.panel}>
        <form className={styles.filters} onSubmit={applyFilters}>
          <label>
            Resource / API
            <input
              value={resource}
              onChange={(event) => setResource(event.target.value)}
              placeholder="e.g. /equipment"
            />
          </label>
          <label>
            User
            <input value={user} onChange={(event) => setUser(event.target.value)} placeholder="Username" />
          </label>
          <label>
            Client IP
            <input value={clientIp} onChange={(event) => setClientIp(event.target.value)} placeholder="e.g. 10.0.0.12" />
          </label>
          <label>
            Calling URL
            <input value={callingUrl} onChange={(event) => setCallingUrl(event.target.value)} placeholder="Origin or referring URL" />
          </label>
          <label>
            From
            <input type="datetime-local" value={startTime} onChange={(event) => setStartTime(event.target.value)} />
          </label>
          <label>
            To
            <input type="datetime-local" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
          </label>
          <div className={styles.filterActions}>
            <button type="submit">Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={clearFilters}>Clear</button>
          </div>
        </form>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <div className={styles.summary}>{loading ? 'Loading…' : `${total} error log${total === 1 ? '' : 's'}`}</div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Client IP</th>
                <th>Status</th>
                <th>Method</th>
                <th>Resource / API</th>
                <th>Calling URL</th>
                <th>Payload & Error</th>
              </tr>
            </thead>
            <tbody>
              {!loading && logs.length === 0 ? (
                <tr><td colSpan="8" className={styles.empty}>No error logs match the filters.</td></tr>
              ) : logs.map((log) => (
                <tr key={log.id}>
                  <td className={styles.nowrap}>{new Date(log.occurred_at).toLocaleString()}</td>
                  <td>{log.user || 'Anonymous'}</td>
                  <td className={styles.nowrap}>{log.client_ip || '—'}</td>
                  <td><span className={styles.status}>{log.status_code}</span></td>
                  <td>{log.method}</td>
                  <td className={styles.resource}>{log.resource}</td>
                  <td className={styles.callingUrl} title={log.calling_url || ''}>{log.calling_url || '—'}</td>
                  <td>
                    <details>
                      <summary>Received payload</summary>
                      <pre>{JSON.stringify(log.request_payload, null, 2)}</pre>
                    </details>
                    <details>
                      <summary>Error details</summary>
                      <pre>{JSON.stringify(log.error_details, null, 2)}</pre>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next</button>
        </div>
      </section>
    </div>
  );
};

export default SettingsErrorLogsPage;
